import React, { ReactNode } from 'react';
import { differenceInDays } from 'date-fns';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import WarningIcon from '@mui/icons-material/Warning';
import moment from 'moment';
import { Box, Typography } from '@mui/material';

import { DEAL_STAGE, Lead } from '@tassomutuo-backoffice/types';

export type ActionRequiredType =
  | 'appointmentToBeRescheduled'
  | 'oldUpdate'
  | 'appointmentHasPassed'
  | 'appointmentToBeConfirmed'
  | null;

const useLeadActionRequired = () => {
  const renderActionRequired = (
    { lead }: { lead: Lead },
    returnType = false
  ): ReactNode | ActionRequiredType => {
    const dealStage = lead?.properties?.dealstage;
    const lastActionDate = lead?.properties?.notes_last_updated
      ? new Date(lead?.properties?.notes_last_updated)
      : null;
    let appointmentDate = null;
    if (lead?.properties?.appointment_date) {
      if (lead?.properties?.appointment_hour) {
        appointmentDate = new Date(
          `${lead?.properties?.appointment_date} ${lead?.properties?.appointment_hour}`
        );
      } else {
        appointmentDate = new Date(lead?.properties?.appointment_date);
      }
    }

    // Se la lead è DROPPED o CONCLUSA allora non deve tornare nulla
    if (
      dealStage === DEAL_STAGE.DROPPED ||
      dealStage === DEAL_STAGE.ATTO_NOTARILE_CONCLUSO
    ) {
      return null;
    }

    // Ottieni la data attuale
    const now = new Date();

    // Calcola la differenza in giorni tra le due date
    //const diffLastActionInDays = now.diff(lastActionDate, 'days');
    const diffLastActionInDays = lastActionDate
      ? differenceInDays(now, lastActionDate)
      : null;

    // const diffAppointmentInDays = now.diff(appointmentDate, 'days');
    // diffAppointmentInDays <= 0 l'appuntamento non è ancora passato
    // diffAppointmentInDays > 0 l' appuntamento è passato
    const diffAppointmentInDays = appointmentDate
      ? differenceInDays(now, appointmentDate)
      : null;

    let render = null;
    let actionRequiredType: ActionRequiredType = null;

    // LEAD NON AGGIORNATA DA ALMENO 15 GIORNI
    if (diffLastActionInDays && diffLastActionInDays > 15) {
      actionRequiredType = 'oldUpdate';
      render = (
        <>
          {render}

          <Box display="flex" alignItems="center">
            <AccessAlarmIcon fontSize="medium" color="error" />
            <Typography pl={1} color="error" fontWeight="bold">
              {`Sono passati ${diffLastActionInDays} giorni dall'ultima modifica`}
            </Typography>
          </Box>
        </>
      );
    }

    // APPUNTAMENTO DA RIFISSARE
    // appuntamento non passato ma è da confermare
    if (
      diffAppointmentInDays &&
      diffAppointmentInDays <= 0 &&
      dealStage === DEAL_STAGE.APPUNTAMENTO_DA_CONFERMARE
    ) {
      actionRequiredType = 'appointmentToBeConfirmed';

      render = (
        <>
          {render}
          <Box display="flex" alignItems="center">
            <WarningIcon fontSize="medium" color="error" />
            <Typography pl={1} color="error" fontWeight="bold">
              Appuntamento da confermare!
            </Typography>
          </Box>
        </>
      );
    }

    // APPUNTAMENTO PASSATO
    // data appuntamento passato e lo stato è appuntamento confermato o appuntamento da confermare
    if (
      diffAppointmentInDays &&
      diffAppointmentInDays > 0 &&
      (dealStage === DEAL_STAGE.APPUNTAMENTO_DA_CONFERMARE ||
        dealStage === DEAL_STAGE.APPUNTAMENTO_CONFERMATO)
    ) {
      actionRequiredType = 'appointmentHasPassed';

      render = (
        <>
          {render}
          <Box display="flex" alignItems="center">
            <WarningIcon fontSize="medium" color="error" />
            <Typography pl={1} color="error" fontWeight="bold">
              Data appuntamento passata, aggiornare lead!
            </Typography>
          </Box>
        </>
      );
    }

    if (
      dealStage === DEAL_STAGE.APPUNTAMENTO_DA_RIFISSARE ||
      dealStage === DEAL_STAGE.CONTATTARE_CLIENTE
    ) {
      actionRequiredType = 'appointmentToBeRescheduled';

      render = (
        <>
          {render}
          <Box display="flex" alignItems="center">
            <WarningIcon fontSize="medium" color="error" />
            <Typography pl={1} color="error" fontWeight="bold">
              Appuntamento da rifissare!
            </Typography>
          </Box>
        </>
      );
    }

    // APPUNTAMENTO DA CONFERMARE
    // quick fix appuntamento da confermare
    if (!render && dealStage === DEAL_STAGE.APPUNTAMENTO_DA_CONFERMARE) {
      actionRequiredType = 'appointmentToBeConfirmed';

      render = (
        <>
          {render}
          <Box display="flex" alignItems="center">
            <WarningIcon fontSize="medium" color="error" />
            <Typography pl={1} color="error" fontWeight="bold">
              Appuntamento da confermare!
            </Typography>
          </Box>
        </>
      );
    }

    return returnType ? actionRequiredType : render;
  };

  const getActionRequiredCounters = (leads: Lead[]) => {
    return leads.reduce(
      (acc, lead) => {
        const actionRequired = renderActionRequired({ lead }, true);
        if(lead?.properties?.hs_lastmodifieddate!==null)
          acc.lastmodifieddate.push(lead?.properties?.hs_lastmodifieddate);
        if(lead?.properties?.notes_last_updated!==null)
          acc.notes_last_updated.push(lead?.properties?.notes_last_updated);

        if(moment().diff(moment(lead?.properties?.notes_last_updated), 'days') >= 60){ // number of lead not updated since 60 days
          acc.oldest++
        } 

        switch (actionRequired) {
          case 'appointmentToBeRescheduled':
            acc.appointmentToBeReschedule++;
            break;
          case 'oldUpdate':
            acc.oldUpdateLeads++;
            break;
          case 'appointmentHasPassed':
            acc.appointmentHasPassed++;
            break;
          case 'appointmentToBeConfirmed':
            acc.appointmentToBeConfirmed++;
            break;
          default:
            // Handle unexpected actionRequired value, if needed.
            break;
        }

        return acc;
      },
      {
        lastmodifieddate:[],
        notes_last_updated:[],
        appointmentToBeReschedule: 0,
        oldUpdateLeads: 0,
        appointmentHasPassed: 0,
        appointmentToBeConfirmed: 0,
        oldest: 0,
      } as {
        lastmodifieddate: string[],
        notes_last_updated: (string | undefined)[],
        appointmentToBeReschedule: number,
        oldUpdateLeads: number,
        appointmentHasPassed: number,
        appointmentToBeConfirmed: number,
        oldest: number,
      }
    );
  };

  return { renderActionRequired, getActionRequiredCounters };
};

export default useLeadActionRequired;
